// Operations Hub — the desktop (≥1024px) command-side panel. Dark on purpose: the
// crew's board stays a bright job list; the office's rail reads as a control room.
// One staggered reveal on mount, then it sits still (calm-board rule applies here too).
import { downloadBackup } from '../../lib/backup';
import { signOutDevice } from '../../state/admin';
import { showHistory, showJobsDb } from '../../state/adminUi';
import { hubStats } from '../../state/hubstats';
import { week } from '../../state/schedule';
import { viewMode } from '../../state/filters';
import {
  Activity,
  ArchiveList,
  ByTech,
  Kpis,
  People,
  ProgressBar,
  SectionTitle,
} from './HubBlocks';

export function HubSidebar() {
  const s = hubStats.value;
  const w = week.value;

  const navBtn = (active: boolean) =>
    `tap rounded-lg px-3 py-2 text-sm font-bold ${active ? 'bg-brand-600 text-white' : 'bg-white/5 text-white/80 hover:bg-white/10'}`;

  return (
    <aside
      class="hub-rail fixed inset-y-0 left-0 z-20 hidden w-72 flex-col overflow-y-auto overscroll-contain bg-band px-4 pt-4 pb-6 text-white lg:flex"
      aria-label="Operations hub"
      data-testid="hub-sidebar"
    >
      <div class="hub-fade">
        <h2 class="text-lg font-extrabold">
          <span class="text-brand-500">Δ</span>T Operations Hub
        </h2>
        <p class="text-xs font-semibold text-white/50" data-testid="hub-week-label">
          {w ? `${w.weekOf} · ${s.total} calls` : '—'}
        </p>
      </div>

      <div class="hub-fade mt-3" style="--d:1">
        <Kpis s={s} dark />
        <ProgressBar s={s} dark />
      </div>

      <div class="hub-fade mt-3 grid grid-cols-2 gap-1.5" style="--d:2">
        <button class={navBtn(viewMode.value === 'week')} onClick={() => (viewMode.value = 'week')}>
          This Week
        </button>
        <button
          class={navBtn(viewMode.value === 'resolved')}
          onClick={() => (viewMode.value = 'resolved')}
        >
          Resolved
        </button>
        <button
          class="tap col-span-2 rounded-lg bg-white/5 px-3 py-2 text-sm font-bold text-white/80 hover:bg-white/10"
          data-testid="hub-jobs-db"
          onClick={() => (showJobsDb.value = true)}
        >
          Job Database
        </button>
        <button
          class="tap col-span-2 rounded-lg bg-white/5 px-3 py-2 text-sm font-bold text-white/80 hover:bg-white/10"
          data-testid="hub-history"
          onClick={() => (showHistory.value = true)}
        >
          History
        </button>
      </div>

      <div class="hub-fade" style="--d:3">
        <SectionTitle dark>Archived weeks</SectionTitle>
        <ArchiveList dark />
        <SectionTitle dark>By technician</SectionTitle>
        <ByTech s={s} dark />
        <SectionTitle dark>Latest activity</SectionTitle>
        <Activity s={s} dark limit={6} />
        <SectionTitle dark>People</SectionTitle>
        <People dark />
      </div>

      <div class="hub-fade mt-auto space-y-1.5 pt-4" style="--d:4">
        <button
          class="tap w-full rounded-lg bg-white/5 px-3 py-2 text-sm font-bold text-white/80 hover:bg-white/10"
          onClick={() => void downloadBackup()}
        >
          Download backup
        </button>
        <button
          class="tap w-full rounded-lg px-3 py-2 text-sm font-semibold text-white/50 hover:bg-white/5"
          onClick={() => {
            if (confirm('Sign out on this device? You will need an access code to get back in.'))
              void signOutDevice();
          }}
        >
          Sign out on this device
        </button>
      </div>
    </aside>
  );
}
